import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MessageSquare, Globe, Bot, ChevronDown, ChevronUp, CheckCircle2, RotateCcw } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso || "—";
  }
};

export const MessagesInboxAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, lRes] = await Promise.all([
        fetch(`${API}/api/admin/contact-messages`, { headers: authHeaders() }),
        fetch(`${API}/api/admin/chat/leads`, { headers: authHeaders() }),
      ]);
      const contacts = cRes.ok ? await cRes.json() : [];
      const leads = lRes.ok ? await lRes.json() : [];
      const merged = [
        ...contacts.map((c) => ({ ...c, _kind: "contact" })),
        ...leads.map((l) => ({ ...l, _kind: "chat" })),
      ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setItems(merged);
    } catch {
      toast.error("Impossible de charger les messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (item, status) => {
    const url = item._kind === "contact"
      ? `${API}/api/admin/contact-messages/${item.id}`
      : `${API}/api/admin/chat/leads/${item.id}`;
    try {
      const res = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      setItems((prev) => prev.map((i) => (i.id === item.id && i._kind === item._kind ? { ...i, status } : i)));
      toast.success(status === "done" ? "Marqué comme traité" : "Remis en nouveau");
    } catch {
      toast.error("Mise à jour impossible");
    }
  };

  const filtered = items.filter((i) => {
    if (filter === "contact" || filter === "chat") return i._kind === filter;
    if (filter === "new") return (i.status || "new") !== "done";
    return true;
  });

  const newCount = items.filter((i) => (i.status || "new") !== "done").length;

  return (
    <div className="space-y-5" data-testid="messages-inbox-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-medium text-[#1F2A4A]">Messages reçus</h2>
          <p className="text-sm text-[#1F2A4A]/60">
            Formulaire contact & demandes via le chat Aria — {newCount} à traiter
          </p>
        </div>
        <div className="flex gap-2">
          {[
            { v: "all", label: `Tous (${items.length})` },
            { v: "new", label: "À traiter" },
            { v: "contact", label: "Site contact" },
            { v: "chat", label: "Chat" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              data-testid={`messages-filter-${f.v}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                filter === f.v
                  ? "bg-[#1F2A4A] text-white border-[#1F2A4A]"
                  : "bg-white text-[#1F2A4A] border-[#1F2A4A]/20 hover:border-[#1F2A4A]/50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#1F2A4A]/60 py-10 text-center">Chargement…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-[#1F2A4A]/60 py-10 text-center" data-testid="messages-empty">Aucun message.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => {
            const isDone = (item.status || "new") === "done";
            const key = `${item._kind}-${item.id}`;
            const isOpen = expanded === key;
            return (
              <div
                key={key}
                data-testid={`message-row-${item.id}`}
                className={`bg-white border rounded-xl p-4 ${isDone ? "opacity-60 border-[#1F2A4A]/10" : "border-[#1F2A4A]/15"}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <span className={`shrink-0 mt-0.5 w-8 h-8 rounded-full flex items-center justify-center ${item._kind === "contact" ? "bg-[#1F2A4A] text-[#C9B896]" : "bg-[#C9B896] text-[#1F2A4A]"}`}>
                      {item._kind === "contact" ? <Globe className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-[#1F2A4A]">{item.name || "—"}</p>
                        <Badge variant="outline" className={item._kind === "contact" ? "border-[#1F2A4A]/30 text-[#1F2A4A]" : "border-[#C9B896] text-[#8a7340]"}>
                          {item._kind === "contact" ? "Site — page contact" : "Chat Aria"}
                        </Badge>
                        {isDone ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">Traité</Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-800 border border-blue-300">Nouveau</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-[#1F2A4A]/70">
                        {item.email && (
                          <a href={`mailto:${item.email}`} className="inline-flex items-center gap-1 hover:text-[#C9B896]">
                            <Mail className="w-3 h-3" /> {item.email}
                          </a>
                        )}
                        {item.phone && (
                          <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1 hover:text-[#C9B896]">
                            <Phone className="w-3 h-3" /> {item.phone}
                          </a>
                        )}
                        <span>{fmtDate(item.created_at)}</span>
                      </div>
                      {item.subject && <p className="text-xs mt-1 text-[#1F2A4A]/80"><strong>Sujet :</strong> {item.subject}</p>}
                      <p className="text-sm mt-2 text-[#1F2A4A]/90 whitespace-pre-wrap break-words">
                        {item._kind === "contact" ? item.message : (item.question || "—")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {item._kind === "chat" && (item.transcript || []).length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        data-testid={`message-transcript-toggle-${item.id}`}
                        onClick={() => setExpanded(isOpen ? null : key)}
                      >
                        {isOpen ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
                        Conversation
                      </Button>
                    )}
                    {isDone ? (
                      <Button variant="outline" size="sm" className="text-xs" data-testid={`message-reopen-${item.id}`} onClick={() => setStatus(item, "new")}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Rouvrir
                      </Button>
                    ) : (
                      <Button size="sm" className="text-xs bg-[#1F2A4A] text-white hover:bg-[#324166]" data-testid={`message-done-${item.id}`} onClick={() => setStatus(item, "done")}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Traité
                      </Button>
                    )}
                  </div>
                </div>
                {isOpen && item._kind === "chat" && (
                  <div className="mt-4 border-t border-[#1F2A4A]/10 pt-3 space-y-2" data-testid={`message-transcript-${item.id}`}>
                    {(item.transcript || []).map((m, i) => (
                      <div key={i} className={`text-xs rounded-lg px-3 py-2 max-w-[85%] ${m.role === "user" ? "bg-[#1F2A4A]/5 text-[#1F2A4A]" : "bg-[#C9B896]/15 text-[#1F2A4A]/80 ml-auto"}`}>
                        <span className="font-medium">{m.role === "user" ? "Visiteur" : "Aria"} : </span>
                        {m.content}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-2 text-xs text-[#1F2A4A]/50">
        <MessageSquare className="w-3.5 h-3.5" />
        Les candidatures sont suivies dans l'onglet « Candidatures », les devis dans « Devis ».
      </div>
    </div>
  );
};
